export type OneViewSource = {
  operation: string;
  path: string;
  fields: string;
};

/** UI regions that OneView REST (maui.swagger.yaml) can populate. */
export const ONEVIEW_SOURCE = {
  search: {
    operation: "SearchService_Search",
    path: "POST /v1/alpha/search",
    fields: "Name, phone, and corp/house/cust account lookup",
  },
  customerName: {
    operation: "CustomerService_GetCustomerSummary",
    path: "GET /v1/get-customer-summary",
    fields: "Customer.individual first/last name or organization name",
  },
  accountNumber: {
    operation: "AccountService_GetAccount",
    path: "GET /v1/accounts/{accountNumber}",
    fields: "Account.legacy corp, house, cust (xxxxx-xxxxxx-xx)",
  },
  products: {
    operation: "CustomerService_GetCustomerSummary",
    path: "GET /v1/get-customer-summary",
    fields: "AccountSummary.services and devices",
  },
  tenure: {
    operation: "AccountService_GetAccount",
    path: "GET /v1/tenants/{tenant}/customers/{customer}/accounts/{account}",
    fields: "Account.installDate / billDate",
  },
  language: {
    operation: "CustomerService_GetCustomer",
    path: "GET /customerManagement/v1/customer/{name}",
    fields: "Individual.preferences.language",
  },
  standing: {
    operation: "AccountService_GetAccount",
    path: "GET /v1/tenants/{tenant}/customers/{customer}/accounts/{account}",
    fields: "Account.accountStatus",
  },
  autoPay: {
    operation: "AutoPayService_GetAutoPay",
    path: "GET .../accounts/{account}/autopay",
    fields: "AutoPay enrollment status",
  },
  balance: {
    operation: "BillService_GetAccountBalance",
    path: "GET .../accounts/{account}/accountbalance",
    fields: "AccountBalance after recent payments and credits",
  },
  due: {
    operation: "BillService_GetNextPaymentDueInfo",
    path: "GET .../accounts/{account}/nexpaymentdueinfo:next-payment-due-info",
    fields: "Next payment due date",
  },
  lastInvoice: {
    operation: "BillService_ListBills",
    path: "POST /v1/listbills · BillingCareService_ListCustomerBills",
    fields: "Latest customer bill amount and statement date",
  },
  activeServices: {
    operation: "AccountServicesService_GetCurrentServices",
    path: "GET .../accountservices:current · ListServices",
    fields: "Current account services inventory",
  },
  contactMedia: {
    operation: "CustomerService_GetCustomerCPCInfo",
    path: "POST /customers/v1/getcustomercpcinfo",
    fields: "Mobile, email, CPC — CPNI-gated",
  },
  serviceAddress: {
    operation: "AccountService_GetAccount",
    path: "GET /v1/accounts/{accountNumber}",
    fields: "Account.serviceAddress",
  },
  billingAddress: {
    operation: "AccountService_GetAccount",
    path: "GET /v1/accounts/{accountNumber}",
    fields: "Account.billingAddress",
  },
  contactPreference: {
    operation: "ContactPreferenceService_ListContactPreferences",
    path: "GET .../contactpreferences",
    fields: "Preferred contact channel (text, voice, email)",
  },
  paymentHistory: {
    operation: "PaymentService_ListPayments",
    path: "GET .../payments · BillService_GetBillAccountActivity",
    fields: "Payment history and ledger activity",
  },
  contract: {
    operation: "AccountService_GetAccountQuote",
    path: "GET .../accounts/{account}:quote",
    fields: "Quote / agreement on the account",
  },
  authorizedUsers: {
    operation: "CustomerRelationshipService_ListCustomerRelationships",
    path: "GET .../customerrelationships",
    fields: "Related parties and roles on the customer",
  },
  consent: {
    operation: "ContactPreferenceService_GetContactPreference",
    path: "GET .../contactpreferences/{id}",
    fields: "Marketing permissions, alert preferences, bill communication",
  },
  billComm: {
    operation: "BillService_GetBillCommunicationPreference",
    path: "GET .../accounts/{account}:bill-communication-preference",
    fields: "Paperless / bill delivery preference",
  },
  activity: {
    operation: "CustomerInteractionLogService_ListCustomerInteractionLogs",
    path: "GET .../customerinteractionlogs",
    fields: "Maui-stored customer interaction log",
  },
  invoice: {
    operation: "BillService_GetConsolidatedDetails",
    path: "GET .../accounts/{account}:consolidated-details",
    fields: "Bills, payments, and account activity in one call",
  },
  invoicePdf: {
    operation: "BillService_GetBillPDFFile",
    path: "POST /v1/... GetBillPDFFile · DigitalBillService_GetBillSummaryDocument",
    fields: "Invoice PDF / digital bill summary",
  },
  credit: {
    operation: "AdjustmentCreditService_CreateAdjustmentCredit",
    path: "POST .../adjustmentcredits · AdjustmentService_GetCreditInEligibleReason",
    fields: "Apply / list memo credits (role limits still BFF-enforced)",
  },
  payMethod: {
    operation: "PayMethodService_ListPayMethods",
    path: "GET .../paymethods",
    fields: "Tokenized wallet last-4 — never PAN",
  },
  billingTrend: {
    operation: "BillingCareService_ListCustomerBills",
    path: "GET ... ListCustomerBills · BillService_ListBills",
    fields: "Recent statement amounts for trend chart",
  },
  usage: {
    operation: "UsageInfoService_ListUsageInfos",
    path: "GET .../usageinfos",
    fields: "Usage consumption — CPNI-gated",
  },
  mobile: {
    operation: "DeviceService_ListDevices",
    path: "GET .../devices · GetCustomerSummary.devices",
    fields: "Mobile lines, plans, and devices",
  },
  internet: {
    operation: "ServiceService_ListServices",
    path: "GET .../services · NetworkService_GetNetwork · RouterService_GetRouter",
    fields: "Internet service, speed, gateway/router",
  },
  outage: {
    operation: "OutageService_GetOutageInfo",
    path: "GET /v1/getoutageinfo · VerifyOutageOnAccount",
    fields: "Known-issue / outage notice for the account",
  },
  diagnostics: {
    operation: "DeviceService_GetGateWayStatusCheck",
    path: "GET gateway status · RebootDevice · GetGateWayRfCheckStatus",
    fields: "Gateway online/offline, RF check, reboot",
  },
  phone: {
    operation: "PhoneService_ListPhones",
    path: "GET .../phones",
    fields: "Home phone numbers and features",
  },
  tv: {
    operation: "TVService_GetChannelLineup",
    path: "GET channel lineup · DeviceService_ListDevices",
    fields: "TV plan and set-top / streaming devices",
  },
  addons: {
    operation: "AccountServicesService_GetAccountServices",
    path: "GET .../accountservices · GetCurrentServices",
    fields: "Add-ons currently on the account",
  },
} as const satisfies Record<string, OneViewSource>;

export type OneViewSourceKey = keyof typeof ONEVIEW_SOURCE;
